import { t, Trans } from '@lingui/macro'
import { CancelOutlined, EditOutlined, HourglassTop, PersonAddAlt1 } from '@mui/icons-material'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { useContactRoleValues } from 'app/backend/selesforce-custom-queries/opportunity-affiliation'
import { checkContactUpdateRequest, createCaseByFlow } from 'app/services/sfAuth/sfData/sfCase'
import { checkContactByFlow, createContactByFlow, mapContact, saveContactByFlow } from 'app/services/sfAuth/sfData/sfContact'
import { filterUsers } from 'app/services/sfAuth/sfData/sfUser'
import { ActionsColumnHeader } from 'app/views/common-components/ActionsColumnHeader'
import { ContactSelectorDialog } from 'app/views/common-components/contact-selector/ContactSelectorDialog'
import CustomDataTable from 'app/views/common-components/CustomDataTable'
import { ErrorLabel } from 'app/views/common-components/forms/ErrorLabel'
import {
  parseExtensionPhoneToSF
} from 'app/views/common/Formats'
import { FormErrorLabel } from 'app/views/common/labels/FormErrorLabel'
import { getMuState } from 'app/views/forms/common/Common'
import { MUEditedByLabel } from 'app/views/forms/multiuser/components/MUEditedByLabel'
import { endEditingField } from 'app/views/forms/multiuser/grpcMultiuserEdit'
import { muiTextLabels } from 'app/views/utilities/muiDataTablesTranslations'
import * as crypto from 'crypto'
import { useField, useFormikContext } from 'formik'
import { isNil } from 'lodash'
import { useSnackbar } from 'notistack'
import { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { myI18n } from 'translation/I18nConnectedProvider'
import { isDebugUser } from 'utilsTS'
import * as Yup from 'yup'

export const validateContactsRolesAndInfo = ({
  array,
  createError,
  roles,
  path
}) => {
  if (!array) {
    return true
  }

  const validationErrors = []

  array.forEach((obj, index) => {
    if (!obj.contact) {
      validationErrors.push(
        new Yup.ValidationError(
          (<Trans>REQUIRED_CONTACT_ERROR</Trans>),
          null,
          `${path}[${index}].missingContact`
        )
      )
    } else {
      const { firstName, lastName, email, phoneNumber, workPhone, mobilePhone, hasActiveCaseRequest } = obj.contact
      const missingInfo =
        !firstName || !lastName || !email || (!phoneNumber && !workPhone && !mobilePhone)
      if (missingInfo && !hasActiveCaseRequest) {
        validationErrors.push(
          new Yup.ValidationError(
            (<Trans>REQUIRED_INFORMATION_ERROR</Trans>),
            null,
            `${path}[${index}].missingInfo`
          )
        )
      }
    }
  })

  if (validationErrors.length > 0) {
    return createError({ message: () => validationErrors })
  }
  return true
}

Yup.addMethod(Yup.array, 'checkAssociatedContacts', function (item) {
  return this.test('checkAssociatedContacts', null, function (array) {
    const { path, createError } = this
    const { roles } = item.typeProps
    return validateContactsRolesAndInfo({ array, createError, path, roles })
  })
})

export const formAssociatedContactsListValidation = (item, options = {}) => {
  return Yup.array().checkAssociatedContacts(item)
}

export const formProjectAffiliationsExtractSaveKey = ({
  sfObject,
  saveMap,
  value,
  connectedProps,
  item
}) => {
  const { typeProps } = item
  const { connectedObject } = connectedProps
  if (typeProps.roles) {
    typeProps.roles.forEach(role => {
      let { sfField, apiValue } = role
      if (!sfField) {
        // If form was not saved and updated with new functionality we need to determine the field to save
        switch (role.apiValue) {
          case 'First Signer':
            sfField = 'aff_grantee_first_signer__c'
            break
          case 'Second Signer':
            sfField = 'aff_grantee_second_signer__c'
            break
          case 'Primary Contact':
            sfField = 'aff_grantee_primary_contact__c'
            break
          case 'Secondary Contact':
            sfField = 'Secondary_Contact__c'
            break
          case 'Accounting Contact':
            sfField = 'Accounting_Contact__c'
            break
          default:
            break
        }
      }
      const existingValue = value.find(obj => obj?.apiValue === apiValue && obj?.contact?.id)
      if (existingValue) {
        if (sfField) {
          saveMap[connectedObject][sfField] = existingValue.contact.id
        }
      } else if (sfField) {
        saveMap[connectedObject][sfField] = null
      }
    })
  }
}

export const formProjectAffiliaitonsDefaultValue = (
  obj,
  additionalInfo,
  item
) => {
  const { roles = [] } = item.typeProps
  const contacts = additionalInfo?.opportunityAffiliatedContacts || []
  const { contactReviewMap = {} } = additionalInfo || {}
  return roles.map(role => {
    const sfContact = contacts.find(obj => obj.Role__c === role.apiValue && obj.Status__c !== 'Former')
    const mappedContact = sfContact ? mapContact(sfContact.Contact__c) : null
    const contact = mappedContact
      ? { ...mappedContact, hasActiveCaseRequest: contactReviewMap[mappedContact.id] === true }
      : null
    const roleId = 'fake' + crypto.randomBytes(8).toString('hex')
    return {
      ...role,
      id: roleId,
      contact
    }
  })
}

export const formProjectAffiliaitonsValueToText = (v = [], question) => {
  const { roles = [] } = question || {}
  const toRet = langVersion => {
    const roleToTrans = {}
    roles.forEach(role => {
      if (role.apiValue) {
        roleToTrans[role.apiValue] = langVersion === 'fr' ? role.labelFR : role.labelEN
      }
    })
    return (
      <div>
        <Grid container style={{ width: '100%' }}>
          <Grid item xs style={{ padding: 4 }}>
            <Typography className='form-print-subtitle'>
              <Trans>Contact name</Trans>
            </Typography>
          </Grid>
          <Grid item xs style={{ padding: 4 }}>
            <Typography className='form-print-subtitle'>
              <Trans>Contact e-mai</Trans>
            </Typography>
          </Grid>
          <Grid item xs style={{ padding: 4 }}>
            <Typography className='form-print-subtitle'>
              <Trans>Contact role</Trans>
            </Typography>
          </Grid>
        </Grid>
        {v
          .filter(c => !c.deleted)
          .map((role, index) => {
            const contact = role?.contact || {}
            return (
              <Box key={role.id}>
                <Divider />
                <Grid container style={{ width: '100%' }}>
                  <Grid item xs style={{ padding: 4 }}>
                    <Typography>{contact.name}</Typography>
                  </Grid>
                  <Grid item xs style={{ padding: 4 }}>
                    <Typography>{contact.email}</Typography>
                  </Grid>
                  <Grid item xs style={{ padding: 4 }}>
                    <Typography>
                      {roleToTrans[contact?.role] || contact?.role}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )
          })}
      </div>
    )
  }
  if (roles.length === 0) {
    return null
  }

  return {
    fr: toRet(true),
    en: toRet()
  }
}

export const FormProjectAffiliations = ({
  typeProps = {},
  title,
  value = [],
  disabled,
  saveDisabled,
  connectedObject,
  id,
  muBag,
  useMultiuser,
  editMode,
  langVersion
}) => {
  const invalid = Boolean(
    !connectedObject ||
      !connectedObject.Id ||
      connectedObject.attributes.type !== 'Opportunity'
  )
  // const { roles = [], showRelationshipColumn } = typeProps
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const { values, setFieldValue, setFieldTouched, errors } = useFormikContext()
  const [meta] = useField(id)
  const { touched } = meta
  const { isEdited, userColor, userName } = getMuState({ values, id })
  const organization = useSelector(state => state.organization)
  const currentUser = useSelector(state => state.user)
  const organizationId = organization.id
  const theme = useTheme()
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'))
  const [selectedRoleId, setSelectedRoleId] = useState(null)
  const [selectedRoleLabel, setSelectedRoleLabel] = useState(null)
  const dialogRef = useRef(null)

  const contactRoleValues = useContactRoleValues(currentUser?.language)

  const onContactFound = async contact => {
    const selectedRole = value.find(role => role.id === selectedRoleId)
    if (selectedRole?.contact?.id === contact.id) {
      enqueueSnackbar(
        <Trans>This contact is already associated with this role!</Trans>,
        { variant: 'error' }
      )
    } else {
      let hasActiveCaseRequest = false
      if (contact.id && organizationId) {
        hasActiveCaseRequest = Boolean(
          await checkContactUpdateRequest(contact.id, organizationId)
        )
      }
      const contactWithReview = { ...contact, hasActiveCaseRequest }
      const toSet = value.map(role => {
        if (role.id === selectedRoleId) {
          return { ...role, id: selectedRoleId, contact: contactWithReview }
        }
        return role
      })
      setFieldValue(id, [...toSet])
      if (useMultiuser) {
        endEditingField({
          ...muBag,
          fieldId: id,
          fieldValue: [...toSet],
          lockId: values.muInfo.lockId
        })
      }
    }
  }

  const createNewContact = async (contactValues) => {
    const creatingSnackbar = enqueueSnackbar(
      <Trans>Creating new contact</Trans>,
      { variant: 'info', persist: true }
    )
    const toCreate = {
      title: contactValues.jobTitle,
      firstName: contactValues.firstName,
      lastName: contactValues.lastName,
      phone: parseExtensionPhoneToSF(
        contactValues.phoneNumber,
        contactValues.phoneNumberCountryCode
      ),
      email: contactValues.email
    }
    let hasError = false
    try {
      const result = await createContactByFlow(toCreate)
      closeSnackbar(creatingSnackbar)
      if (isNil(result.error) || result.error === '') {
        enqueueSnackbar(
          <Trans>Contact created!</Trans>,
          { variant: 'success' }
        )
        onContactFound({ ...contactValues, id: result.newContactId })
      }
      if (result.error) {
        hasError = true
        let errorTrans = <Trans>Contact with this name already exists!</Trans>
        if (result.error === 'NAME_ALREADY_IN_USE') {
          errorTrans = <Trans>Contact with this name already exists!</Trans>
        }
        if (!isNil(errorTrans)) {
          enqueueSnackbar(errorTrans, { variant: 'error' })
        }
        throw result.error
      }
      return result
    } catch (error) {
      closeSnackbar(creatingSnackbar)
      if (!hasError) {
        enqueueSnackbar(
          <Trans>Error occurred while creating new contact!</Trans>,
          { variant: 'error' }
        )
      }
      throw error
    }
  }

  const updateContact = async ({ contact, isValid, initialValues }) => {
    const isContactOwner = contact.owner === currentUser.userId

    if (isContactOwner) {
      const snackbar = enqueueSnackbar(<Trans>UPDATING_CONTACT_SNACKBAR_TITLE</Trans>, { variant: 'info' })
      try {
        await saveContactByFlow(contact)
        closeSnackbar(snackbar)
        enqueueSnackbar(<Trans>Contact updated!</Trans>, { variant: 'info' })
        onContactUpdate(contact)
      } catch (error) {
        closeSnackbar(snackbar)
        console.error(error)
        enqueueSnackbar(
          <Trans>Error ocurred while updating contact!</Trans>,
          { variant: 'error' }
        )
        throw error
      }
    } else {
      let body = myI18n._(t`NEW_CONTACT_WAS_SUBMITTED_BY`) + ' ' + currentUser.displayName + ', ' + currentUser?.userObject?.jobTitle + '\n\n'

      const textData = [
        { key: 'firstName', text: myI18n._(t`First name`) },
        { key: 'lastName', text: myI18n._(t`Last name`) },
        { key: 'email', text: myI18n._(t`Email`) },
        { key: 'changeReason', text: myI18n._(t`CHANGE_REASON_INPUT_LABEL`) }

      ]
      textData.forEach(obj => {
        const { key, text } = obj
        if (contact[key] !== initialValues[key]) {
          body += text + ': ' + contact[key] + '\n'
        }
      })
      if (
        contact.phoneNumber !== initialValues.phoneNumber ||
          contact.phoneNumberCountryCode !== initialValues.phoneNumberCountryCode
      ) {
        body += myI18n._(t`Phone number`) +
          ': ' + parseExtensionPhoneToSF(contact.phoneNumber, contact.phoneNumberCountryCode)
      }
      const toCreate = {
        title: 'Update contact information',
        type: 'Request contact information update',
        contact: contact.id,
        opportunityId: connectedObject.Id,
        language: currentUser.language,
        userName: currentUser.displayName,
        organization: organization.id,
        description: body
      }
      const programManager = connectedObject.Assigned_Program_Manager__c
      if (programManager) {
        toCreate.owner = programManager
        toCreate.skipAssigment = true
      }
      const snackbar = enqueueSnackbar(<Trans>SUBMITTING_CONTACT_TO_REVIEW_SNACKBAR_TITLE</Trans>, { variant: 'info' })
      try {
        await createCaseByFlow(toCreate)
        if (contact?.id) {
          const toSet = value.map(role => {
            if (role.contact?.id === contact.id) {
              return { ...role, contact: { ...role.contact, hasActiveCaseRequest: true } }
            }
            return role
          })
          setFieldValue(id, toSet)
        }
        closeSnackbar(snackbar)
        enqueueSnackbar(<Trans>Information submitted!</Trans>, { variant: 'info' })
      } catch (error) {
        closeSnackbar(snackbar)
        enqueueSnackbar(<Trans>Error Submitting Case</Trans>, { variant: 'error' })
        console.error(error)
        throw error
      }
    }
  }

  if (invalid && !editMode) {
    return (
      <div style={{ padding: 10, color: 'red' }}>
        <Trans>
          There is no object connected in editor or connected object is not of
          "Opportunity" type!
        </Trans>
      </div>
    )
  }

  const onContactUpdate = newValues => {
    const toSet = value.map(role => {
      if (role.id === selectedRoleId) {
        return {
          ...role,
          id: selectedRoleId,
          contact: newValues
        }
      }
      return role
    })
    setFieldValue(id, [...toSet])
    if (useMultiuser) {
      endEditingField({
        ...muBag,
        fieldId: id,
        fieldValue: [...toSet],
        lockId: values.muInfo.lockId
      })
    }
  }

  const searchContacts = async ({ values }) => {
    const flow = await checkContactByFlow(values)
    const result = flow[0].outputValues
    const filteredUsers = filterUsers({
      currentUser,
      users: result.contactsFound,
      disallowedStrings: isDebugUser(currentUser)
        ? ['DUPLICATE']
        : ['FAKE', 'TESTER', 'DUPLICATE']
    })
    if (filteredUsers?.length === 0) {
      return { contacts: [], error: 'NOT_FOUND' }
    }
    if (result.error != null) {
      return { contacts: [], error: result.error }
    }
    const parsedContacts = filteredUsers.map((contact) => mapContact(contact))
    return { contacts: parsedContacts, error: null }
  }

  const editIconButton = (roleId, roleLabel) => (
    <Tooltip title={<Trans>Edit</Trans>}>
      <IconButton
        onClick={() => {
          setSelectedRoleId(roleId)
          setSelectedRoleLabel(roleLabel)
          dialogRef.current?.open('contact-info')
        }}
        sx={{
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: '#17374F1A',
            color: 'primary.main'
          }
        }}
        disabled={disabled}
      >
        <EditOutlined />
      </IconButton>
    </Tooltip>
  )

  const activeRequestIconButton = (
    <Tooltip title={<Trans>CONTACT_INFO_REVIEWED_TOOLTIP</Trans>}>
      <IconButton
        sx={{
          borderRadius: '4px',
          cursor: 'auto',
          backgroundColor: '#17374F1A',
          color: 'primary.main',
          '&:hover': {
            backgroundColor: '#17374F1A',
            color: 'primary.main'
          }
        }}
        disabled={disabled}
      >
        <HourglassTop />
      </IconButton>
    </Tooltip>
  )

  const deleteContactButton = (roleId) => {
    return (
      <IconButton
        // disabled={disabled || saveDisabled}
        onClick={() => {
          // const toSet = [...field.value]
          const toSet = value.map(role => {
            if (role.id === roleId) {
              return {
                ...role,
                id: roleId,
                contact: null
              }
            }
            return role
          })
          // toSet.some((obj, index) => {
          //   const bool = obj.id === value.id
          //   if (bool) {
          //     const item = { ...obj }
          //     item.deleted = true
          //     toSet[index] = item
          //   }
          //   return bool
          // })
          setFieldValue(id, [...toSet])
          if (useMultiuser) {
            endEditingField({
              ...muBag,
              fieldId: id,
              fieldValue: [...toSet],
              lockId: values.muInfo.lockId
            })
          }
          if (!touched) {
            setFieldTouched(id)
          }
        }}
      >
        <DeleteOutlinedIcon />
      </IconButton>
    )
  }

  const fieldErrors = errors[id]
  const showRowErrors = Array.isArray(fieldErrors)

  const tableData = value.map((role, index) => {
    const contactName = role.contact?.firstName && role.contact?.lastName
      ? `${role.contact.firstName} ${role.contact.lastName}`
      : ''
    return {
      contactSelector: {
        apiValue: role.apiValue,
        id: role.id,
        contactId: role.contact?.id,
        hasActiveCaseRequest: role.contact?.hasActiveCaseRequest === true
      },
      contactName,
      email: role.contact?.email,
      actions: {
        id: role.id,
        contact: role.contact,
        apiValue: role.apiValue
      }
    }
  })

  return (
    <>
      <CustomDataTable
        data={tableData}
        columns={[
          {
            name: 'contactSelector',
            options: {
              sort: false,
              customHeadLabelRender: props => {
                return (
                  <Box sx={{ fontWeight: 600 }}>
                    <Trans>CONTACT_LABEL</Trans>{' *'}
                  </Box>
                )
              },
              customBodyRender: (value, tableMeta, updateValue) => {
                const roleId = value.id
                const apiValue = value.apiValue
                const rowIdx = tableMeta.rowIndex
                const rowError = showRowErrors ? fieldErrors[rowIdx] : null
                const roleLabel = contactRoleValues?.find(role => role.value === apiValue)?.label
                if (value?.contactId) {
                  return (
                    <Box>
                      <Box
                        sx={{ fontWeight: 700, color: '#17374F' }}
                      >
                        {roleLabel} *
                      </Box>
                      {rowError?.missingInfo && !value.hasActiveCaseRequest && (
                        <ErrorLabel
                          sx={{ mt: 1 }}
                          label={(
                            <Grid container alignItems='center'>
                              <Grid item sx={{ height: '16px' }}>
                                <CancelOutlined sx={{ color: '#E51C40', mr: 1, fontSize: '16px' }} />
                              </Grid>
                              <Grid item>{rowError?.missingInfo}</Grid>
                            </Grid>
                          )}
                        />
                      )}
                    </Box>
                  )
                }
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '214px', width: 'fit-content' }}>
                    <Button
                      disabled={disabled}
                      color='primary'
                      variant='contained'
                      sx={{ fontSize: '14px', lineHeight: '19px', width: '100%' }}
                      onClick={(e) => {
                        setSelectedRoleId(roleId)
                        setSelectedRoleLabel(roleLabel)
                        dialogRef.current?.open('search')
                      }}
                    >
                      <PersonAddAlt1 sx={{ mr: 1, fontSize: '15px', color: 'white' }} />
                      <Trans>ADD_FRAGMENT_TEXT</Trans>
                      <Box sx={{ ml: '6px' }}>{roleLabel}</Box>
                    </Button>
                    {rowError?.missingContact && (
                      <ErrorLabel
                        label={(
                          <Grid container alignItems='center'>
                            <Grid item sx={{ height: '16px' }}>
                              <CancelOutlined sx={{ color: '#E51C40', mr: 1, fontSize: '16px' }} />
                            </Grid>
                            <Grid item>{rowError?.missingContact}</Grid>
                          </Grid>
                        )}
                        sx={{
                          mt: 1,
                          width: '100%',
                          '.error-label-text-grid': {
                            width: '100%'
                          }
                        }}
                      />
                    )}
                  </Box>
                )
              }
            }
          },
          {
            name: 'contactName',
            options: {
              customHeadLabelRender: props => <><Trans>Name</Trans>{' *'}</>
            }
          },
          {
            name: 'email',
            options: {
              customHeadLabelRender: props => <><Trans>E-mail</Trans>{' *'}</>
            }
          },
          {
            name: 'actions',
            label: myI18n._(t`Actions`),
            options: {
              sort: false,
              setCellHeaderProps: () => isLargeScreen ? ({ style: { width: '1%', whiteSpace: 'nowrap' } }) : {},
              setCellProps: () => isLargeScreen ? ({ style: { width: '1%', whiteSpace: 'nowrap' } }) : {},
              customHeadLabelRender: props =>
                <ActionsColumnHeader />,
              customBodyRender: (value, tableMeta, updateValue) => {
                const { contact, id, apiValue } = value
                const isContactFound = !isNil(contact?.id)
                const hasActiveCaseRequest = contact?.hasActiveCaseRequest === true
                const roleLabel = contactRoleValues?.find(role => role.value === apiValue)?.label
                return (
                  <Grid container wrap='nowrap' justifyContent='flex-end' gap={1}>
                    {isContactFound && (
                      <Grid item>
                        {(contact?.owner === currentUser.userId || !hasActiveCaseRequest) && editIconButton(id, roleLabel)}
                        {hasActiveCaseRequest && contact?.owner !== currentUser.userId && activeRequestIconButton}
                      </Grid>
                    )}
                    {contact?.id && deleteContactButton(id)}
                  </Grid>
                )
              }
            }
          }
        ]}
        options={{
          textLabels: muiTextLabels(myI18n),
          search: false,
          filter: false,
          selectableRows: 'none',
          print: false,
          download: false,
          viewColumns: false
        }}
      />
      <ContactSelectorDialog
        ref={dialogRef}
        values={value.find(role => role.id === selectedRoleId)?.contact}
        createNewContact={createNewContact}
        searchContacts={searchContacts}
        updateContact={updateContact}
        onSelect={onContactFound}
        onDialogClose={() => setSelectedRoleId(null)}
        type='project-affiliations'
        role={selectedRoleLabel}
      />
      <FormErrorLabel error={meta.error} id={id} />
      {isEdited && (
        <div style={{ marginTop: 5 }}>
          <MUEditedByLabel color={userColor} userName={userName} />
        </div>
      )}
    </>
  )
}
